import {
  Button,
  FormField,
  FormGrid,
  IconButton,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import Dropzone, { ImageFile } from 'react-dropzone';

import { AudioPlayer } from '../audioPlayer/audioPlayer';

import type { UploadCallback } from './announcementPicker';
import styles from './dialog.module.scss';
import { TitleError, TitleInput } from './titleInput';

const MAX_FILE_SIZE = 20097152; //20097152 ~20mb.
const ALLOWED_FILE_TYPES = 'video/webm, audio/*';

interface Props {
  onClose: VoidFunction;
  onUpload: UploadCallback;
}

export function UploadDialog({ onClose, onUpload }: Props): JSX.Element {
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState<TitleError | null>(null);
  const [file, setFile] = useState<ImageFile | null>(null);

  const { preview } = file || {};

  const onDrop = useCallback(
    ([file]: File[]) => {
      setFile(file);
    },
    [setFile],
  );
  const onDeleteClick = useCallback(() => {
    setFile(null);
  }, [setFile]);
  const onUploadClick = useCallback(() => {
    if (!file) {
      return;
    }

    if (!title) {
      setTitleError(TitleError.MissingTitle);

      return;
    }

    const { type, lastModified } = file;
    const fileWithTitle = new File([file], title, { type, lastModified });

    onUpload(fileWithTitle);
    onClose();
  }, [onUpload, onClose, title, file]);

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <Text.Heading variant="heading-3">Upload new audio file</Text.Heading>
        <IconButton variant="tertiary" icon="close" onClick={onClose} />
      </div>
      <FormGrid className={styles.form}>
        <FormField>
          {file ? (
            <AudioPlayer
              src={preview}
              filename={title}
              onDelete={onDeleteClick}
            />
          ) : (
            <Dropzone
              className={styles.dropzone}
              onDropAccepted={onDrop}
              accept={ALLOWED_FILE_TYPES}
              maxSize={MAX_FILE_SIZE}
            />
          )}
        </FormField>
        <FormField>
          <TitleInput
            value={title}
            onChange={setTitle}
            errorCode={titleError}
          />
        </FormField>
      </FormGrid>
      <div className={styles.foot}>
        <Button variant="primary" disabled={!file} onClick={onUploadClick}>
          Upload
        </Button>
      </div>
    </div>
  );
}
